import PostItCard from "@bmi-digital/components/post-it-card";
import React, { useMemo } from "react";
import ProductFeaturesTable from "../../components/ProductFeaturesTable";
import RichText from "../../components/RichText";
import Link from "../../components/link/Link";
import { StyledLeadBlockContent } from "../../components/styles/ProductLeadBlock.styles";
import {
  StyledGrid,
  StyledLeadBlockContentSection,
  StyledTechnicalSpecificationLeadBlock
} from "./styles/technicalSpecificationLeadBlock.styles";
import type { SpecificationNotesWithCta } from "./systemDetailsPage";
import type { Classification } from "../../types/pim";

type Props = {
  technicalSpecClassifications: readonly Classification[];
  specificationNotes: SpecificationNotesWithCta | null;
};

const TechnicalSpecificationLeadBlock = ({
  technicalSpecClassifications,
  specificationNotes
}: Props) => {
  const sortedClassifications = useMemo(
    () =>
      [...technicalSpecClassifications]
        .sort((a, b) => (a.name > b.name ? 1 : -1))
        .map(({ features, ...rest }) => ({
          ...rest,
          features: [...features].sort((a, b) => a.name!.localeCompare(b.name!))
        })),
    [technicalSpecClassifications]
  );

  return (
    <StyledTechnicalSpecificationLeadBlock>
      <StyledLeadBlockContent>
        <StyledLeadBlockContentSection>
          {sortedClassifications.length ? (
            <ProductFeaturesTable classifications={sortedClassifications} />
          ) : null}
        </StyledLeadBlockContentSection>
      </StyledLeadBlockContent>
      {specificationNotes && (
        <StyledGrid lg={4} xs={12} data-testid="specificationNotes">
          <PostItCard color="pearl">
            <PostItCard.Section>
              <PostItCard.Heading hasUnderline>
                {specificationNotes.title}
              </PostItCard.Heading>
              <PostItCard.Content>
                <RichText
                  document={specificationNotes.description}
                  gtmLabel={specificationNotes.title}
                />
              </PostItCard.Content>
              <PostItCard.Action>
                {specificationNotes.cta && (
                  <Link
                    data={specificationNotes.cta[0]}
                    gtm={{
                      id: "cta-click1",
                      label: specificationNotes.title
                        ? `${specificationNotes.title} - ${specificationNotes.cta[0].label}`
                        : specificationNotes.cta[0].label,

                      // TODO: https://bmigroup.atlassian.net/browse/DXB-10445 Why are we only populating the action gtm label when specificationNotes.cta is an external link? In Payload, you can define all link types
                      action:
                        "url" in specificationNotes.cta[0]
                          ? specificationNotes.cta[0].url
                          : ""
                    }}
                  >
                    {specificationNotes.cta[0].label}
                  </Link>
                )}
              </PostItCard.Action>
            </PostItCard.Section>
          </PostItCard>
        </StyledGrid>
      )}
    </StyledTechnicalSpecificationLeadBlock>
  );
};

export default TechnicalSpecificationLeadBlock;
